import React, { useEffect, useRef, useState } from "react";
import * as palette from "../Variables";
import styled from "styled-components";

const StyledStickyNav = styled.div`
    width: 150px;
    height: 300px;
    position: sticky;
    top: ${props => props.headerOffset || 72}px;
    z-index: 1;
    margin: 72px auto 72px 40px;
    display: none;
    flex-flow: column nowrap;
    align-items: flex-start;
    justify-content: center;
    transition: top 300ms ease;

    @media (min-width: 1200px) {
        display: flex;
    }

    a {
        font-weight: 400;
        text-decoration: none;
        transition: font-size 200ms;
        height: 40px;
        display: flex;
        flex-flow: row nowrap;
        align-items: center;

        &:hover, &:focus-visible {
            font-weight: 600 !important;
            font-size: 1.08em;
            text-decoration: underline !important;
            text-underline-offset: 4px;
        }

        &:focus-visible {
            outline: 3px solid ${palette.blueMain};
            outline-offset: 4px;
            border-radius: 4px;
        }

        &:hover:not(.on-dark), &:focus-visible:not(.on-dark) {
            color: ${palette.pinkMain} !important;
        }
    }
`;

const navLinks = [
    { href: "#overview", label: "Overview" },
    { href: "#general-research", label: "General Research" },
    { href: "#user-research", label: "User Research" },
    { href: "#challenge", label: "Challenge" },
    { href: "#analysis", label: "Analysis" },
    { href: "#iterations", label: "Iterations" },
    { href: "#final-design", label: "Final Design" },
    { href: "#reflections", label: "Reflections" },
];

const StickyNav = ({ headerOffset = 72 }) => {
    const navRef = useRef(null);
    const linkRefs = useRef([]);
    const [linkOnDark, setLinkOnDark] = useState(Array(navLinks.length).fill(false));
    const [activeSection, setActiveSection] = useState(null);

    useEffect(() => {
        const handleScroll = () => {
            const sections = Array.from(document.querySelectorAll("section"));

            // check if each link is over a dark section
            const newLinkOnDark = navLinks.map((_, idx) => {
                const link = linkRefs.current[idx];
                if (!link) return false;
                const linkRect = link.getBoundingClientRect();
                const linkMidY = linkRect.top + linkRect.height / 2;
                for (const section of sections) {
                    const sectionRect = section.getBoundingClientRect();
                    if (linkMidY >= sectionRect.top && linkMidY <= sectionRect.bottom) {
                        return section.classList.contains("dark-background");
                    }
                }
                return false;
            });

            // find the section closest to the top of the viewport
            let currentSection = null;
            let minDistance = Infinity;
            sections.forEach(section => {
                const rect = section.getBoundingClientRect();
                const distance = Math.abs(rect.top - headerOffset);
                if ((rect.top - headerOffset) <= 0 && distance < minDistance) {
                    minDistance = distance;
                    currentSection = section.id;
                }
            });
            if (!currentSection && sections.length > 0) {
                currentSection = sections[0].id;
            }

            setLinkOnDark(newLinkOnDark);
            setActiveSection(currentSection ? `#${currentSection}` : null);
        };

        window.addEventListener("scroll", handleScroll, { passive: true });
        window.addEventListener("resize", handleScroll);
        handleScroll();

        return () => {
            window.removeEventListener("scroll", handleScroll);
            window.removeEventListener("resize", handleScroll);
        };
    }, [headerOffset]);

    return (
        <StyledStickyNav ref={navRef} headerOffset={headerOffset}>
            {navLinks.map((link, idx) => (
                <a
                    key={link.href}
                    href={link.href}
                    ref={el => linkRefs.current[idx] = el}
                    className={[
                        link.href === activeSection ? "active-link" : "",
                        linkOnDark[idx] ? "on-dark" : ""
                    ].join(" ")}
                    style={{
                        color:
                            link.href === activeSection
                                ? (linkOnDark[idx] ? palette.neutral1 : palette.neutral11)
                                : (linkOnDark[idx] ? palette.neutral1 : palette.neutral9),
                        fontWeight: link.href === activeSection ? 600 : 400,
                        textDecoration: link.href === activeSection ? "underline" : "none",
                        textUnderlineOffset: "4px",
                        transition: "font-size 200ms"
                    }}
                >
                    {link.label}
                </a>
            ))}
        </StyledStickyNav>
    );
};

export default StickyNav;